import { HolderOutlined } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Table, Tooltip } from "antd";
import { AIQuestionItem } from "paper-splitter/interfaces";
import React, { cloneElement, useEffect, useState } from "react";
import { CSS } from "@dnd-kit/utilities";

import "./index.css";
import { Link, useLocation } from "react-router-dom";

type DraggableTableProps = {
  data: AIQuestionItem[];
  onDataChange: (data: AIQuestionItem[]) => void;
};

const Row = ({ children, recentlyMovedId, ...props }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      }
    )?.replace(/translate3d\(([^,]+),/, "translate3d(0,"),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };

  const isRecentlyMoved = props["data-row-key"] === recentlyMovedId;

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      className={isRecentlyMoved ? "row-flash" : ""}
      {...attributes}
    >
      {children &&
        children.length &&
        children.map((child: any) => {
          if (child.key === "sort") {
            return cloneElement(child, {
              children: (
                <HolderOutlined
                  ref={setActivatorNodeRef}
                  style={{
                    touchAction: "none",
                    cursor: "grab",
                  }}
                  {...listeners}
                />
              ),
            });
          }
          return child;
        })}
    </tr>
  );
};

export const DraggableTable: React.FC<DraggableTableProps> = ({
  data,
  onDataChange,
}) => {
  const location = useLocation();
  const [recentlyMovedId, setRecentlyMovedId] = React.useState<string | null>(
    null
  );
  const [questions, setQuestions] = useState<AIQuestionItem[]>([]);

  useEffect(() => {
    setQuestions(data);
  }, [data]);

  const onDragEnd = ({ active, over }: any) => {
    if (!over) return;

    const oldIndex = questions.findIndex((item) => item.id === active.id);
    const newIndex = questions.findIndex((item) => item.id === over.id);

    if (oldIndex === newIndex) return;

    const newData = arrayMove(questions, oldIndex, newIndex).map(
      (question, index) => {
        question.ai_group_sequence = index + 1;
        return question;
      }
    );

    onDataChange(newData);
    setRecentlyMovedId(active.id);

    setTimeout(() => setRecentlyMovedId(null), 300);
  };

  const columns = [
    {
      key: "sort",
    },
    {
      title: "Sequence",
      dataIndex: "ai_group_sequence",
      render: (value: number, row: any) => {
        return (
          <>
            {row.additional ? (
              <Tooltip title="Additional">
                <span style={{ fontWeight: 600, marginLeft: 4 }}>{value}*</span>
              </Tooltip>
            ) : (
              value
            )}
          </>
        );
      },
    },
    {
      title: "Question",
      dataIndex: "title",
    },
    {
      title: "Paper",
      dataIndex: "paper",
      render: (value: string, row: any) => {
        return (
          <Link to={`${window.location.origin}/papers/${value}?question=${row.id}`} target="_blank">
            {value}
          </Link>
        );
      },
    },
    {
      title: "Points",
      dataIndex: "points_possible",
    },
  ];

  return (
    <DndContext onDragEnd={onDragEnd}>
      <SortableContext
        items={questions.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <Table
          components={{
            body: {
              row: (props: any) => (
                <Row {...props} recentlyMovedId={recentlyMovedId} />
              ),
            },
          }}
          rowKey="id"
          columns={columns}
          dataSource={questions}
          pagination={false}
          scroll={{ y: 620 }}
        />
      </SortableContext>
    </DndContext>
  );
};
